import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import Checkbox from "components/common/Checkbox";
import AdminFamilySelect from "components/creation/AdminFamilySelect";
import FeedEntryComponent from "components/activity/FeedEntryComponent";
import Select from "components/common/Select";
import TextField from "components/common/TextField";
import { useSearchParams } from "react-router-dom";
import { useGetFamilyDetails } from "services/advisor";
import {
  useGetAccountBotDetails,
  useEnsureCoreDocuments,
  useAdminCreateAgentCron,
  useAdminUpdateAgentCron,
} from "services/admin";
import { useGetBotConversationFeed } from "services/feed";
import { useGetDocument, useUpdateDocument } from "services/documents";
import { Family } from "protogen/advisors_service_pb";
import { FeedEntry } from "protogen/feed_pb";
import { AccountStub } from "protogen/common_pb";
import {
  AdminCreateAgentCronRequest,
  AdminUpdateAgentCronRequest,
  GetAccountBotDetailsResponse_BotConversation,
  GetAccountBotDetailsResponse_AgentCron,
  GetAccountBotDetailsResponse_BotDocument,
  GetAccountBotDetailsResponse_BotTask,
  GetAccountBotDetailsResponse_BotCalendarEvent,
  GetAccountBotDetailsResponse_BotDelegation,
  GetAccountBotDetailsResponse_TokenSummary,
} from "protogen/admin_service_pb";
import { AddedBotTask, AddedBotEvent } from "protogen/feed_service_pb";
import { Document, DocumentStatus, DocumentType } from "protogen/documents_pb";
import { UpdateDocumentRequest } from "protogen/documents_service_pb";
import { FeedFocusState } from "types/feed";
import { Attachment } from "protogen/common_pb";
import AttachmentDialog from "components/common/AttachmentDialog";
import ConfirmationDialog, {
  useConfirmationDialog,
} from "components/common/ConfirmationDialog";
import DateDisplay from "components/common/DateDisplay";
import useIsMobile from "components/hooks/useIsMobile";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";
import AgentDebugTool from "components/tasks/blocks/AgentDebugTool";
import DocumentViewer from "components/editor/DocumentViewer";
import ListDocumentView from "components/documents/ListDocumentView";
import DropdownMenu from "components/common/DropdownMenu";
import LinkRouter from "components/navigation/LinkRouter";
import {
  Bug,
  ChevronDown,
  CircleCheckBig,
  Calendar,
  Cpu,
  ExternalLink,
  FilePlus,
  Handshake,
  MapPin,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const EMPTY_FOCUS_STATE: FeedFocusState = {
  selectedContact: {} as any,
  selectedMedium: null,
  replyToEmail: null,
  editEmailDraft: null,
  selectedEntryKey: null,
  isDirty: false,
  groupText: false,
  groupRecipientRefs: [],
};

const BOT_COLORS = {
  botResponse: "#EFF6FF",
  botError: "#FEF2F2",
  userMessage: "#F3F4F6",
  iconColor: "#3B82F6",
  linkColor: "primary.main",
};

const EXECUTION_STATUS_COPY: Record<string, string> = {
  completed: "Completed",
  failed: "Failed",
  executing: "Running",
  preparing: "Preparing",
  idle: "Idle",
};

const EXECUTION_STATUS_COLOR: Record<string, string> = {
  completed: "#15803D",
  failed: "#B91C1C",
  executing: "#1D4ED8",
  preparing: "#7C3AED",
  idle: "#6B7280",
};

const RECURRING_UNITS = [
  { value: "", label: "One-time (no recurrence)" },
  { value: "minute", label: "Minutes" },
  { value: "hour", label: "Hours" },
  { value: "day", label: "Days" },
  { value: "week", label: "Weeks" },
  { value: "month", label: "Months" },
];

const CreateAgentCronDialog = ({
  familyRef,
  loading,
  onClose,
  onCreate,
}: {
  familyRef: string;
  loading: boolean;
  onClose: () => void;
  onCreate: (params: AdminCreateAgentCronRequest) => void;
}) => {
  const [title, setTitle] = useState("");
  const [goal, setGoal] = useState("");
  const [showSchedule, setShowSchedule] = useState(false);
  const [dateStr, setDateStr] = useState("");
  const [timeStr, setTimeStr] = useState("09:00");
  const [useUserTimezone, setUseUserTimezone] = useState(true);
  const [showRecurrence, setShowRecurrence] = useState(false);
  const [recurringUnit, setRecurringUnit] = useState("");
  const [recurringInterval, setRecurringInterval] = useState(1);

  const canSubmit = title.trim() && goal.trim();

  const handleCreate = () => {
    if (!canSubmit) return;
    let firstRunAt = "";
    if (showSchedule && dateStr) {
      firstRunAt = useUserTimezone
        ? `${dateStr}T${timeStr}`
        : new Date(`${dateStr}T${timeStr}`).toISOString();
    }
    onCreate(
      new AdminCreateAgentCronRequest({
        familyRef,
        title: title.trim(),
        goal: goal.trim(),
        firstRunAt,
        recurringUnit: showRecurrence && recurringUnit ? recurringUnit : "",
        recurringInterval:
          showRecurrence && recurringUnit ? recurringInterval : 0,
        useUserTimezone,
      }),
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Scheduled Action</DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          pt: "8px !important",
        }}
      >
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          size="small"
          placeholder="e.g. Weekly calendar review"
        />
        <TextField
          label="Goal"
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
          placeholder="Goal for the agent when it runs. What is it looking to accomplish?"
        />
        <Box>
          <Typography
            variant="bodySmall"
            onClick={() => setShowSchedule(!showSchedule)}
            sx={{
              color: "#3B82F6",
              cursor: "pointer",
              userSelect: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {showSchedule
              ? "▾ Hide schedule options"
              : "▸ Schedule for later (defaults to now)"}
          </Typography>
          {showSchedule && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                mt: "8px",
              }}
            >
              <Box sx={{ display: "flex", gap: "12px" }}>
                <TextField
                  label="Date"
                  type="date"
                  value={dateStr}
                  onChange={(e) => setDateStr(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Time"
                  type="time"
                  value={timeStr}
                  onChange={(e) => setTimeStr(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useUserTimezone}
                    onChange={(e) => setUseUserTimezone(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="bodySmall">User's timezone</Typography>
                }
              />
            </Box>
          )}
        </Box>
        <Box>
          <Typography
            variant="bodySmall"
            onClick={() => setShowRecurrence(!showRecurrence)}
            sx={{
              color: "#3B82F6",
              cursor: "pointer",
              userSelect: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {showRecurrence
              ? "▾ Hide recurrence options"
              : "▸ Make recurring (defaults to one-time)"}
          </Typography>
          {showRecurrence && (
            <Box
              sx={{
                display: "flex",
                gap: "12px",
                alignItems: "flex-end",
                mt: "8px",
              }}
            >
              <FormControl size="small" sx={{ flex: 2 }}>
                <Select
                  variant="detachedOutlined"
                  value={recurringUnit}
                  displayEmpty
                  sx={{ input: { height: "60px" } }}
                  onChange={(e) => setRecurringUnit(e.target.value)}
                >
                  {RECURRING_UNITS.map((u) => (
                    <MenuItem key={u.value} value={u.value}>
                      {u.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {recurringUnit && (
                <TextField
                  label="Every"
                  type="number"
                  value={recurringInterval}
                  onChange={(e) =>
                    setRecurringInterval(
                      Math.max(1, parseInt(e.target.value) || 1),
                    )
                  }
                  size="small"
                  sx={{ flex: 1 }}
                  InputProps={{ inputProps: { min: 1 } }}
                />
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!canSubmit || loading}
        >
          {loading ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const UpdateAgentCronDialog = ({
  agentCron,
  loading,
  onClose,
  onUpdate,
}: {
  agentCron: GetAccountBotDetailsResponse_AgentCron;
  loading: boolean;
  onClose: () => void;
  onUpdate: (params: AdminUpdateAgentCronRequest) => void;
}) => {
  const hasSchedule = Number(agentCron.nextRunSec) > 0;
  const hasRecurrence = !!agentCron.recurringUnit;
  const nextRunDate = hasSchedule
    ? new Date(Number(agentCron.nextRunSec) * 1000)
    : null;

  const [title, setTitle] = useState(agentCron.title);
  const [goal, setGoal] = useState(agentCron.goal);
  const [showSchedule, setShowSchedule] = useState(hasSchedule);
  const [dateStr, setDateStr] = useState(
    nextRunDate ? nextRunDate.toISOString().slice(0, 10) : "",
  );
  const [timeStr, setTimeStr] = useState(
    nextRunDate ? nextRunDate.toTimeString().slice(0, 5) : "09:00",
  );
  const [useUserTimezone, setUseUserTimezone] = useState(true);
  const [showRecurrence, setShowRecurrence] = useState(hasRecurrence);
  const [recurringUnit, setRecurringUnit] = useState(
    agentCron.recurringUnit || "",
  );
  const [recurringInterval, setRecurringInterval] = useState(
    agentCron.recurringInterval || 1,
  );

  const canSubmit = title.trim() && goal.trim();

  const handleUpdate = () => {
    if (!canSubmit) return;
    let firstRunAt = "";
    if (showSchedule && dateStr) {
      firstRunAt = useUserTimezone
        ? `${dateStr}T${timeStr}`
        : new Date(`${dateStr}T${timeStr}`).toISOString();
    }
    onUpdate(
      new AdminUpdateAgentCronRequest({
        agentCronRef: agentCron.agentCronRef,
        title: title.trim(),
        goal: goal.trim(),
        firstRunAt,
        recurringUnit: showRecurrence && recurringUnit ? recurringUnit : "",
        recurringInterval:
          showRecurrence && recurringUnit ? recurringInterval : 0,
        useUserTimezone,
      }),
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Scheduled Action</DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          pt: "8px !important",
        }}
      >
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          size="small"
        />
        <TextField
          label="Goal"
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
        />
        <Box>
          <Typography
            variant="bodySmall"
            onClick={() => setShowSchedule(!showSchedule)}
            sx={{
              color: "#3B82F6",
              cursor: "pointer",
              userSelect: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {showSchedule ? "▾ Hide schedule options" : "▸ Change schedule"}
          </Typography>
          {showSchedule && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                mt: "8px",
              }}
            >
              <Box sx={{ display: "flex", gap: "12px" }}>
                <TextField
                  label="Date"
                  type="date"
                  value={dateStr}
                  onChange={(e) => setDateStr(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Time"
                  type="time"
                  value={timeStr}
                  onChange={(e) => setTimeStr(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useUserTimezone}
                    onChange={(e) => setUseUserTimezone(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="bodySmall">User's timezone</Typography>
                }
              />
            </Box>
          )}
        </Box>
        <Box>
          <Typography
            variant="bodySmall"
            onClick={() => setShowRecurrence(!showRecurrence)}
            sx={{
              color: "#3B82F6",
              cursor: "pointer",
              userSelect: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {showRecurrence
              ? "▾ Hide recurrence options"
              : "▸ Change recurrence"}
          </Typography>
          {showRecurrence && (
            <Box
              sx={{
                display: "flex",
                gap: "12px",
                alignItems: "flex-end",
                mt: "8px",
              }}
            >
              <FormControl size="small" sx={{ flex: 2 }}>
                <Select
                  variant="detachedOutlined"
                  size="small"
                  value={recurringUnit}
                  displayEmpty
                  onChange={(e) => setRecurringUnit(e.target.value)}
                  sx={{ input: { height: "60px" } }}
                >
                  {RECURRING_UNITS.map((u) => (
                    <MenuItem key={u.value} value={u.value}>
                      {u.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {recurringUnit && (
                <TextField
                  label="Every"
                  type="number"
                  value={recurringInterval}
                  onChange={(e) =>
                    setRecurringInterval(
                      Math.max(1, parseInt(e.target.value) || 1),
                    )
                  }
                  size="small"
                  sx={{ flex: 1 }}
                  InputProps={{ inputProps: { min: 1 } }}
                />
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdate}
          disabled={!canSubmit || loading}
        >
          {loading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FamilyAddress = ({ address }: { address: string }) => {
  try {
    const addr = JSON.parse(address);
    if (!addr?.formattedAddress) return null;
    const query =
      addr.latitude && addr.longitude
        ? `${addr.latitude},${addr.longitude}`
        : addr.formattedAddress;
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          marginTop: "-8px",
        }}
      >
        <MapPin size={14} color="#6B7280" style={{ flexShrink: 0 }} />
        <Link
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            query,
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontSize: "13px", color: "#198282" }}
        >
          {addr.formattedAddress}
        </Link>
      </Box>
    );
  } catch {
    return null;
  }
};

type SidebarSelection =
  | { type: "conversation"; ref: string }
  | { type: "agentCron"; ref: string }
  | { type: "document"; ref: string }
  | { type: "tasks" }
  | { type: "calendarEvents" }
  | { type: "delegations" };

const TokenRow = ({
  label,
  color,
  bg,
  tokens,
  cost,
  showPricing,
}: {
  label: string;
  color: string;
  bg: string;
  tokens: number;
  cost: number;
  showPricing: boolean;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "8px",
    }}
  >
    <Box
      sx={{
        fontSize: "11px",
        fontWeight: 500,
        color,
        backgroundColor: bg,
        borderRadius: "4px",
        padding: "2px 6px",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
    <Typography
      sx={{
        fontSize: "12px",
        color: "#111827",
        fontWeight: 500,
        textAlign: "right",
      }}
    >
      {showPricing ? `$${cost.toFixed(4)}` : tokens.toLocaleString()}
    </Typography>
  </Box>
);

const TokenSummaryPanel = ({
  tokenSummary,
  showPricing,
  onToggle,
}: {
  tokenSummary: GetAccountBotDetailsResponse_TokenSummary;
  showPricing: boolean;
  onToggle: () => void;
}) => {
  const llmRows = [
    {
      label: "↓ Input",
      color: "#1565C0",
      bg: "#E3F2FD",
      tokens: Number(tokenSummary.inputTokens),
      cost: Number(tokenSummary.inputCostUsd),
    },
    {
      label: "↑ Output",
      color: "#2E7D32",
      bg: "#E8F5E9",
      tokens: Number(tokenSummary.outputTokens),
      cost: Number(tokenSummary.outputCostUsd),
    },
    {
      label: "📦 Cache writes",
      color: "#6A1B9A",
      bg: "#F3E5F5",
      tokens: Number(tokenSummary.cacheCreationTokens),
      cost: Number(tokenSummary.cacheCreationCostUsd),
    },
    {
      label: "⚡ Cache reads",
      color: "#E65100",
      bg: "#FFF3E0",
      tokens: Number(tokenSummary.cacheReadTokens),
      cost: Number(tokenSummary.cacheReadCostUsd),
    },
  ].filter((r) => r.tokens > 0);

  const imageRows = [
    {
      label: "↓ Input",
      color: "#1565C0",
      bg: "#E3F2FD",
      tokens: Number(tokenSummary.imageInputTokens),
      cost: Number(tokenSummary.imageInputCostUsd),
    },
    {
      label: "↑ Output",
      color: "#2E7D32",
      bg: "#E8F5E9",
      tokens: Number(tokenSummary.imageOutputTokens),
      cost: Number(tokenSummary.imageOutputCostUsd),
    },
  ].filter((r) => r.tokens > 0);

  const imageTotalCost =
    Number(tokenSummary.imageInputCostUsd) +
    Number(tokenSummary.imageOutputCostUsd);
  const totalCost = Number(tokenSummary.estimatedCostUsd) + imageTotalCost;
  const hasImageUsage = imageRows.length > 0;

  return (
    <Box
      sx={{
        padding: "10px 12px",
        borderRadius: "8px",
        border: "1px solid #E5E7EB",
        backgroundColor: "#F9FAFB",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="bodySmall" color="text.secondary">
          Token Usage (last 30 days)
        </Typography>
        <Typography
          sx={{
            fontSize: "11px",
            color: "#198282",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
          onClick={onToggle}
        >
          {showPricing ? "show tokens" : "show $"}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          borderBottom: "1px solid #E5E7EB",
          pb: "6px",
        }}
      >
        {[
          {
            label: "Threads",
            value: Number(tokenSummary.executionCount30d).toLocaleString(),
          },
          {
            label: "Avg execs / thread",
            value: Number(tokenSummary.avgExecutionsPerThread30d).toFixed(1),
          },
          {
            label: "Avg tokens / thread",
            value: Math.round(
              Number(tokenSummary.avgTokensPerExecution30d),
            ).toLocaleString(),
          },
        ].map(({ label, value }) => (
          <Box key={label} sx={{ flex: "1 1 0", whiteSpace: "nowrap" }}>
            <Typography sx={{ fontSize: "10px", color: "#6B7280" }}>
              {label}
            </Typography>
            <Typography
              sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        {llmRows.map((row) => (
          <TokenRow key={row.label} {...row} showPricing={showPricing} />
        ))}
        {hasImageUsage && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                mt: "4px",
                mb: "2px",
              }}
            >
              <Box
                sx={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }}
              />
              <Typography
                sx={{
                  fontSize: "10px",
                  color: "#9CA3AF",
                  whiteSpace: "nowrap",
                }}
              >
                Image creation tokens
              </Typography>
              <Box
                sx={{ flex: 1, height: "1px", backgroundColor: "#E5E7EB" }}
              />
            </Box>
            {imageRows.map((row) => (
              <TokenRow
                key={`img-${row.label}`}
                {...row}
                showPricing={showPricing}
              />
            ))}
          </>
        )}
        {showPricing && (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                borderTop: "1px solid #E5E7EB",
                pt: "4px",
                mt: "2px",
              }}
            >
              <Typography
                sx={{ fontSize: "12px", color: "#374151", fontWeight: 600 }}
              >
                Total
              </Typography>
              <Typography
                sx={{ fontSize: "12px", color: "#111827", fontWeight: 600 }}
              >
                ${totalCost.toFixed(4)}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: "10px", color: "#9CA3AF" }}>
              estimated, based on model pricing
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default () => {
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFamily, setSelectedFamily] = useState<Family | null>(null);
  const [conversations, setConversations] = useState<
    GetAccountBotDetailsResponse_BotConversation[]
  >([]);
  const [agentCrons, setAgentCrons] = useState<
    GetAccountBotDetailsResponse_AgentCron[]
  >([]);
  const [botDocuments, setBotDocuments] = useState<
    GetAccountBotDetailsResponse_BotDocument[]
  >([]);
  const [botTasks, setBotTasks] = useState<
    GetAccountBotDetailsResponse_BotTask[]
  >([]);
  const [botCalendarEvents, setBotCalendarEvents] = useState<
    GetAccountBotDetailsResponse_BotCalendarEvent[]
  >([]);
  const [botDelegations, setBotDelegations] = useState<
    GetAccountBotDetailsResponse_BotDelegation[]
  >([]);
  const [tokenSummary, setTokenSummary] =
    useState<GetAccountBotDetailsResponse_TokenSummary | null>(null);
  const [showTokenPricing, setShowTokenPricing] = useState(false);
  const [selection, setSelection] = useState<SidebarSelection | null>(null);
  const [entries, setEntries] = useState<FeedEntry[]>([]);
  const [accountMap, setAccountMap] = useState<Record<string, AccountStub>>({});
  const [feedFocusState, setFeedFocusState] =
    useState<FeedFocusState>(EMPTY_FOCUS_STATE);
  const [nextCursor, setNextCursor] = useState<string>("");
  const [hasMore, setHasMore] = useState(false);
  const [agentThreadRefs, setAgentThreadRefs] = useState<
    Record<string, string>
  >({});
  const [debugThreadRef, setDebugThreadRef] = useState<string | null>(null);
  const [openAttachment, setOpenAttachment] = useState<Attachment | null>(null);
  const [addedTasks, setAddedTasks] = useState<AddedBotTask[]>([]);
  const [addedEvents, setAddedEvents] = useState<AddedBotEvent[]>([]);
  const navigate = useNavigate();
  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >({
    tokenUsage: true,
    documents: true,
    scheduledActions: true,
    artifacts: true,
    delegations: true,
  });
  const toggleSection = (key: string) =>
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const selectedConversationRef =
    selection?.type === "conversation" ? selection.ref : null;
  const selectedAgentCron = agentCrons.find(
    (item) =>
      selection?.type === "agentCron" && item.conversationRef === selection.ref,
  );
  const selectedDocumentRef =
    selection?.type === "document" ? selection.ref : null;

  const { request: getFamilyDetails } = useGetFamilyDetails();
  const { request: getAccountBotDetails, loading: detailsLoading } =
    useGetAccountBotDetails();
  const { request: getBotConversationFeed, loading: feedLoading } =
    useGetBotConversationFeed();
  const { request: getDocument, loading: documentLoading } = useGetDocument();
  const { request: updateDocument } = useUpdateDocument();
  const { request: ensureCoreDocuments, loading: ensureCoreDocsLoading } =
    useEnsureCoreDocuments();
  const { request: adminCreateAgentCron, loading: createCronLoading } =
    useAdminCreateAgentCron();
  const { request: adminUpdateAgentCron, loading: updateCronLoading } =
    useAdminUpdateAgentCron();
  const confirmState = useConfirmationDialog();
  const { registerHandler, unregisterHandler } = useWebSocketContext();
  const [showCreateCron, setShowCreateCron] = useState(false);
  const [editingCron, setEditingCron] =
    useState<GetAccountBotDetailsResponse_AgentCron | null>(null);

  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null,
  );
  const [documentContent, setDocumentContent] = useState<string | null>(null);
  const [documentAttachments, setDocumentAttachments] = useState<Attachment[]>(
    [],
  );
  const [documentTitle, setDocumentTitle] = useState<string>("");

  const feedContainerRef = useRef<HTMLDivElement>(null);
  const selectionStable = useRef(selection);
  selectionStable.current = selection;
  const pendingDocRef = useRef<string | null>(null);

  useEffect(() => {
    if (!selectedFamily?.ref) return;
    const familyRef = selectedFamily.ref;
    const channel = "textmessage/";
    const handler = () => {
      loadDetails(familyRef, false);
      const sel = selectionStable.current;
      if (sel?.type === "conversation") {
        loadFeed(sel.ref);
      }
    };
    registerHandler(channel, handler);
    return () => {
      unregisterHandler(channel, handler);
    };
  }, [selectedFamily?.ref, registerHandler, unregisterHandler]);

  useEffect(() => {
    const familyRef = searchParams.get("familyRef");
    if (familyRef) {
      const loadFamily = async () => {
        const response = await getFamilyDetails({ familyRef });
        if (response?.family) {
          setSelectedFamily(response.family);
        }
      };
      loadFamily();
    } else {
      setSelectedFamily(null);
    }
  }, [searchParams.get("familyRef")]);

  useEffect(() => {
    if (selectedFamily?.ref) {
      loadDetails(selectedFamily.ref);
    } else {
      setConversations([]);
      setAgentCrons([]);
      setBotDocuments([]);
      setBotTasks([]);
      setBotCalendarEvents([]);
      setBotDelegations([]);
      setTokenSummary(null);
      setSelection(null);
      setEntries([]);
    }
  }, [selectedFamily?.ref]);

  useEffect(() => {
    if (!selection) {
      setEntries([]);
      setAccountMap({});
      setAgentThreadRefs({});
      setAddedTasks([]);
      setAddedEvents([]);
      setSelectedDocument(null);
      setDocumentContent(null);
      setDocumentAttachments([]);
      setDocumentTitle("");
      return;
    }
    const params: Record<string, string> = {};
    const familyRef = searchParams.get("familyRef");
    if (familyRef) params.familyRef = familyRef;

    if (selection.type === "conversation") {
      params.conversationRef = selection.ref;
      setSearchParams(params, { replace: true });
      loadFeed(selection.ref);
      setSelectedDocument(null);
      setDocumentContent(null);
    } else if (selection.type === "agentCron") {
      params.conversationRef = selection.ref;
      setSearchParams(params, { replace: true });
      setEntries([]);
      setAccountMap({});
      setAgentThreadRefs({});
      setAddedTasks([]);
      setAddedEvents([]);
      setHasMore(false);
      setNextCursor("");
      setSelectedDocument(null);
      setDocumentContent(null);
    } else if (selection.type === "document") {
      params.documentRef = selection.ref;
      setSearchParams(params, { replace: true });
      setEntries([]);
      setSelectedDocument(null);
      setDocumentContent(null);
      setDocumentAttachments([]);
      setDocumentTitle("");
      loadDocument(selection.ref);
    } else {
      setSearchParams(params, { replace: true });
      setEntries([]);
      setAccountMap({});
      setAgentThreadRefs({});
      setAddedTasks([]);
      setAddedEvents([]);
      setHasMore(false);
      setNextCursor("");
      setSelectedDocument(null);
      setDocumentContent(null);
    }
  }, [selection?.type, (selection as any)?.ref]);

  const loadDetails = async (familyRef: string, resetSelection = true) => {
    const resp = await getAccountBotDetails({ familyRef });
    if (resp) {
      setConversations(resp.conversations);
      setAgentCrons(resp.agentCrons);
      setBotDocuments(resp.botDocuments);
      setBotTasks(resp.botTasks);
      setBotCalendarEvents(resp.botCalendarEvents);
      setBotDelegations(resp.botDelegations);
      setTokenSummary(resp.tokenSummary || null);

      if (!resetSelection) return;

      const urlConvRef = searchParams.get("conversationRef");
      const urlDocRef = searchParams.get("documentRef");

      if (urlDocRef) {
        const docMatch = resp.botDocuments.find(
          (d) => d.documentRef === urlDocRef,
        );
        if (docMatch) {
          setSelection({ type: "document", ref: docMatch.documentRef });
          return;
        }
      }
      if (urlConvRef) {
        const convMatch = resp.conversations.find(
          (c) => c.conversationRef === urlConvRef,
        );
        if (convMatch) {
          setSelection({
            type: "conversation",
            ref: convMatch.conversationRef,
          });
          return;
        }
        const cronMatch = resp.agentCrons.find(
          (c) => c.conversationRef === urlConvRef,
        );
        if (cronMatch) {
          setSelection({ type: "agentCron", ref: cronMatch.conversationRef });
          return;
        }
      }
      if (resp.conversations.length > 0) {
        const byRecency = [...resp.conversations].sort(
          (a, b) => Number(b.lastUpdatedSec) - Number(a.lastUpdatedSec),
        );
        const smsConv = byRecency.find(
          (c) => c.medium === "sms_bot" || c.medium === "linq_imessage",
        );
        const pick = smsConv ?? byRecency[0];
        setSelection({
          type: "conversation",
          ref: pick.conversationRef,
        });
      } else if (resp.agentCrons.length > 0) {
        setSelection({
          type: "agentCron",
          ref: resp.agentCrons[0].conversationRef,
        });
      } else if (resp.botDocuments.length > 0) {
        setSelection({
          type: "document",
          ref: resp.botDocuments[0].documentRef,
        });
      } else {
        setSelection(null);
      }
    }
  };

  const loadFeed = async (conversationRef: string, cursor?: string) => {
    const resp = await getBotConversationFeed({
      conversationRef,
      startCursor: cursor || "",
      pageSize: 50,
    });
    if (resp) {
      const rewritten = resp.entries.map((entry) => {
        if (entry.textMessage?.content?.includes("app.findfaye.com")) {
          entry.textMessage.content = entry.textMessage.content.replaceAll(
            "app.findfaye.com",
            "advisor.findfaye.com",
          );
        }
        return entry;
      });
      const newEntries = cursor ? [...entries, ...rewritten] : rewritten;
      setEntries(newEntries);
      const newAccountMap = { ...accountMap };
      for (const account of resp.accounts) {
        newAccountMap[account.ref] = account;
      }
      setAccountMap(newAccountMap);
      setAgentThreadRefs(
        cursor
          ? { ...agentThreadRefs, ...resp.messageAgentThreadRefs }
          : resp.messageAgentThreadRefs,
      );
      setAddedTasks(
        cursor ? [...addedTasks, ...resp.addedTasks] : resp.addedTasks,
      );
      setAddedEvents(
        cursor ? [...addedEvents, ...resp.addedEvents] : resp.addedEvents,
      );
      setNextCursor(resp.nextCursor);
      setHasMore(resp.hasMore);
    }
  };

  const loadDocument = async (documentRef: string) => {
    pendingDocRef.current = documentRef;
    const resp = await getDocument({
      documentRef,
      documentType: 0,
      isFormDefinition: false,
      markAsRead: false,
    });
    if (resp?.document && pendingDocRef.current === documentRef) {
      setSelectedDocument(resp.document);
      setDocumentTitle(resp.document.title);
      setDocumentContent(resp.document.note?.content?.payload || "");
      setDocumentAttachments(resp.document.note?.content?.attachments || []);
    }
  };

  const handleDeleteDocument = () => {
    if (!selectedDocumentRef) return;
    confirmState.openDialog(async () => {
      await updateDocument(
        new UpdateDocumentRequest({
          documentRef: selectedDocumentRef,
          status: DocumentStatus.DELETED,
          prevHash: "",
        }),
      );
      setSelection(null);
      setBotDocuments((prev) =>
        prev.filter((doc) => doc.documentRef !== selectedDocumentRef),
      );
    });
  };

  const handleFamilyChange = (
    familyRef: string | null,
    family?: Family | null,
  ) => {
    setSelectedFamily(family || null);
    if (familyRef) {
      setSearchParams({ familyRef }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const handleEnsureCoreDocuments = async () => {
    if (!selectedFamily?.ref) return;
    await ensureCoreDocuments({ familyRef: selectedFamily.ref });
    loadDetails(selectedFamily.ref);
  };

  const handleLoadMore = () => {
    if (selectedConversationRef && hasMore && nextCursor) {
      loadFeed(selectedConversationRef, nextCursor);
    }
  };

  const tasksByMessage = addedTasks.reduce(
    (acc, t) => {
      (acc[t.messageRef] ??= []).push(t);
      return acc;
    },
    {} as Record<string, AddedBotTask[]>,
  );
  const eventsByMessage = addedEvents.reduce(
    (acc, e) => {
      (acc[e.messageRef] ??= []).push(e);
      return acc;
    },
    {} as Record<string, AddedBotEvent[]>,
  );

  const sortedEntries = [...entries].sort(
    (a, b) => Number(b.timestampSec) - Number(a.timestampSec),
  );

  const smsBotConversations = useMemo(
    () =>
      [...conversations]
        .filter((c) => c.medium === "sms_bot" || c.medium === "linq_imessage")
        .sort((a, b) => Number(b.lastUpdatedSec) - Number(a.lastUpdatedSec)),
    [conversations],
  );
  const inAppBotConversations = useMemo(
    () =>
      [...conversations]
        .filter((c) => c.medium === "app-agent")
        .sort((a, b) => Number(b.lastUpdatedSec) - Number(a.lastUpdatedSec)),
    [conversations],
  );

  const sidebar = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        width: isMobile ? "100%" : "280px",
        flexShrink: 0,
        paddingTop: "8px",
        minHeight: 0,
      }}
    >
      <AdminFamilySelect
        selectedFamily={selectedFamily?.ref || null}
        onChange={handleFamilyChange}
        title="Family"
      />
      {selectedFamily && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "-12px",
            }}
          >
            <LinkRouter
              to={`/families/${encodeURIComponent(selectedFamily.ref)}`}
              sx={{ fontSize: "14px" }}
              useColorScheme
            >
              View family
            </LinkRouter>
            {!detailsLoading && (
              <DropdownMenu
                buttonContent="Actions"
                variant="text"
                size="small"
                dropdownIcon
                options={[
                  {
                    title: ensureCoreDocsLoading
                      ? "Creating core memories..."
                      : "Create missing core memories",
                    IconComponent: FilePlus,
                    onClick: handleEnsureCoreDocuments,
                    disabled: ensureCoreDocsLoading,
                  },
                ]}
                sx={{
                  color: "#6B7280",
                  fontSize: "13px",
                  padding: "2px 8px",
                }}
              />
            )}
          </Box>
          {selectedFamily.address && (
            <FamilyAddress address={selectedFamily.address} />
          )}
        </>
      )}

      <Box
        sx={{
          overflowY: "auto",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {tokenSummary &&
          (tokenSummary.inputTokens > 0 || tokenSummary.outputTokens > 0) && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Box
                onClick={() => toggleSection("tokenUsage")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <ChevronDown
                  size={14}
                  color="#6B7280"
                  style={{
                    transform: collapsedSections.tokenUsage
                      ? "rotate(-90deg)"
                      : "none",
                    transition: "transform 0.15s",
                  }}
                />
                <Typography variant="bodySmall" color="text.secondary">
                  Token Usage
                </Typography>
              </Box>
              {!collapsedSections.tokenUsage && (
                <TokenSummaryPanel
                  tokenSummary={tokenSummary}
                  showPricing={showTokenPricing}
                  onToggle={() => setShowTokenPricing((v) => !v)}
                />
              )}
            </Box>
          )}

        {(smsBotConversations.length > 0 ||
          inAppBotConversations.length > 0) && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {smsBotConversations.length > 0 && (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <Box
                  onClick={() => toggleSection("botConversationsSms")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <ChevronDown
                    size={14}
                    color="#6B7280"
                    style={{
                      transform: collapsedSections.botConversationsSms
                        ? "rotate(-90deg)"
                        : "none",
                      transition: "transform 0.15s",
                    }}
                  />
                  <Typography variant="bodySmall" color="text.secondary">
                    SMS ({smsBotConversations.length})
                  </Typography>
                </Box>
                {!collapsedSections.botConversationsSms &&
                  smsBotConversations.map((conversation) => {
                    const selected =
                      selection?.type === "conversation" &&
                      selection.ref === conversation.conversationRef;
                    return (
                      <Box
                        key={conversation.conversationRef}
                        onClick={() =>
                          setSelection({
                            type: "conversation",
                            ref: conversation.conversationRef,
                          })
                        }
                        sx={{
                          padding: "10px 12px",
                          borderRadius: "8px",
                          border: selected
                            ? "1px solid #93C5FD"
                            : "1px solid #E5E7EB",
                          backgroundColor: selected ? "#EFF6FF" : "#FFFFFF",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: selected ? "#DBEAFE" : "#F9FAFB",
                          },
                        }}
                      >
                        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                          {conversation.title}
                        </Typography>
                      </Box>
                    );
                  })}
              </Box>
            )}
            {inAppBotConversations.length > 0 && (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <Box
                  onClick={() => toggleSection("botConversationsInApp")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <ChevronDown
                    size={14}
                    color="#6B7280"
                    style={{
                      transform: collapsedSections.botConversationsInApp
                        ? "rotate(-90deg)"
                        : "none",
                      transition: "transform 0.15s",
                    }}
                  />
                  <Typography variant="bodySmall" color="text.secondary">
                    In app ({inAppBotConversations.length})
                  </Typography>
                </Box>
                {!collapsedSections.botConversationsInApp &&
                  inAppBotConversations.map((conversation) => {
                    const selected =
                      selection?.type === "conversation" &&
                      selection.ref === conversation.conversationRef;
                    return (
                      <Box
                        key={conversation.conversationRef}
                        onClick={() =>
                          setSelection({
                            type: "conversation",
                            ref: conversation.conversationRef,
                          })
                        }
                        sx={{
                          padding: "10px 12px",
                          borderRadius: "8px",
                          border: selected
                            ? "1px solid #93C5FD"
                            : "1px solid #E5E7EB",
                          backgroundColor: selected ? "#EFF6FF" : "#FFFFFF",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: selected ? "#DBEAFE" : "#F9FAFB",
                          },
                        }}
                      >
                        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                          {conversation.title}
                        </Typography>
                      </Box>
                    );
                  })}
              </Box>
            )}
          </Box>
        )}

        {botDocuments.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Box
              onClick={() => toggleSection("documents")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <ChevronDown
                size={14}
                color="#6B7280"
                style={{
                  transform: collapsedSections.documents
                    ? "rotate(-90deg)"
                    : "none",
                  transition: "transform 0.15s",
                }}
              />
              <Typography variant="bodySmall" color="text.secondary">
                Bot Documents ({botDocuments.length})
              </Typography>
            </Box>
            {!collapsedSections.documents &&
              botDocuments.map((doc) => {
                const selected =
                  selection?.type === "document" &&
                  selection.ref === doc.documentRef;
                return (
                  <Box
                    key={doc.documentRef}
                    onClick={() =>
                      setSelection({ type: "document", ref: doc.documentRef })
                    }
                    sx={{
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: selected
                        ? "1px solid #93C5FD"
                        : "1px solid #E5E7EB",
                      backgroundColor: selected ? "#EFF6FF" : "#FFFFFF",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: selected ? "#DBEAFE" : "#F9FAFB",
                      },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "6px" }}
                    >
                      <Cpu size={14} color="#6B7280" />
                      <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                        {doc.title}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
          </Box>
        )}

        {selectedFamily &&
          (() => {
            const sortedCrons = [...agentCrons].sort((a, b) => {
              const aHas = a.scheduleSummary ? 1 : 0;
              const bHas = b.scheduleSummary ? 1 : 0;
              if (aHas !== bHas) return bHas - aHas;
              return Number(b.lastUpdatedSec) - Number(a.lastUpdatedSec);
            });
            const visibleCrons = sortedCrons.slice(0, 3);
            const overflowCrons = sortedCrons.slice(3);
            const renderCronItem = (
              agentCron: GetAccountBotDetailsResponse_AgentCron,
            ) => {
              const selected =
                selection?.type === "agentCron" &&
                selection.ref === agentCron.conversationRef;
              return (
                <Box
                  key={agentCron.agentCronRef}
                  onClick={() =>
                    setSelection({
                      type: "agentCron",
                      ref: agentCron.conversationRef,
                    })
                  }
                  sx={{
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: selected
                      ? "1px solid #93C5FD"
                      : "1px solid #E5E7EB",
                    backgroundColor: selected ? "#EFF6FF" : "#FFFFFF",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: selected ? "#DBEAFE" : "#F9FAFB",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                      {agentCron.title}
                    </Typography>
                    {agentCron.cronType && (
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 500,
                          color:
                            agentCron.cronType === "system"
                              ? "#7C3AED"
                              : agentCron.cronType === "admin"
                                ? "#D97706"
                                : "#6B7280",
                          backgroundColor:
                            agentCron.cronType === "system"
                              ? "#F5F3FF"
                              : agentCron.cronType === "admin"
                                ? "#FFFBEB"
                                : "#F3F4F6",
                          borderRadius: "4px",
                          padding: "1px 6px",
                          flexShrink: 0,
                        }}
                      >
                        {agentCron.cronType}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    sx={{ fontSize: "12px", color: "#6B7280", mt: "2px" }}
                  >
                    {agentCron.scheduleSummary || "No schedule info"}
                  </Typography>
                </Box>
              );
            };
            return (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    onClick={() => toggleSection("scheduledActions")}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <ChevronDown
                      size={14}
                      color="#6B7280"
                      style={{
                        transform: collapsedSections.scheduledActions
                          ? "rotate(-90deg)"
                          : "none",
                        transition: "transform 0.15s",
                      }}
                    />
                    <Typography variant="bodySmall" color="text.secondary">
                      Scheduled Actions ({agentCrons.length})
                    </Typography>
                  </Box>
                  <Tooltip title="Create a scheduled action">
                    <IconButton
                      size="small"
                      onClick={() => setShowCreateCron(true)}
                      sx={{ padding: "2px" }}
                    >
                      <Plus size={16} color="#6B7280" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {!collapsedSections.scheduledActions && (
                  <>
                    {visibleCrons.map(renderCronItem)}
                    {overflowCrons.length > 0 &&
                      !collapsedSections.allCrons && (
                        <Typography
                          variant="bodySmall"
                          onClick={() => toggleSection("allCrons")}
                          sx={{
                            color: "#6B7280",
                            cursor: "pointer",
                            userSelect: "none",
                            fontSize: "12px",
                            textAlign: "center",
                            "&:hover": { color: "#3B82F6" },
                          }}
                        >
                          See all ({overflowCrons.length} more)
                        </Typography>
                      )}
                    {collapsedSections.allCrons &&
                      overflowCrons.map(renderCronItem)}
                  </>
                )}
              </Box>
            );
          })()}

        {(botTasks.length > 0 || botCalendarEvents.length > 0) && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Box
              onClick={() => toggleSection("artifacts")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <ChevronDown
                size={14}
                color="#6B7280"
                style={{
                  transform: collapsedSections.artifacts
                    ? "rotate(-90deg)"
                    : "none",
                  transition: "transform 0.15s",
                }}
              />
              <Typography variant="bodySmall" color="text.secondary">
                Artifacts
              </Typography>
            </Box>
            {!collapsedSections.artifacts && (
              <>
                {botTasks.length > 0 && (
                  <Box
                    onClick={() => setSelection({ type: "tasks" })}
                    sx={{
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border:
                        selection?.type === "tasks"
                          ? "1px solid #93C5FD"
                          : "1px solid #E5E7EB",
                      backgroundColor:
                        selection?.type === "tasks" ? "#EFF6FF" : "#FFFFFF",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor:
                          selection?.type === "tasks" ? "#DBEAFE" : "#F9FAFB",
                      },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "6px" }}
                    >
                      <CircleCheckBig size={14} color="#6B7280" />
                      <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                        All tasks ({botTasks.length})
                      </Typography>
                    </Box>
                  </Box>
                )}
                {botCalendarEvents.length > 0 && (
                  <Box
                    onClick={() => setSelection({ type: "calendarEvents" })}
                    sx={{
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border:
                        selection?.type === "calendarEvents"
                          ? "1px solid #93C5FD"
                          : "1px solid #E5E7EB",
                      backgroundColor:
                        selection?.type === "calendarEvents"
                          ? "#EFF6FF"
                          : "#FFFFFF",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor:
                          selection?.type === "calendarEvents"
                            ? "#DBEAFE"
                            : "#F9FAFB",
                      },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "6px" }}
                    >
                      <Calendar size={14} color="#6B7280" />
                      <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                        All events ({botCalendarEvents.length})
                      </Typography>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {botDelegations.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Box
              onClick={() => toggleSection("delegations")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <ChevronDown
                size={14}
                color="#6B7280"
                style={{
                  transform: collapsedSections.delegations
                    ? "rotate(-90deg)"
                    : "none",
                  transition: "transform 0.15s",
                }}
              />
              <Typography variant="bodySmall" color="text.secondary">
                Delegations ({botDelegations.length})
              </Typography>
            </Box>
            {!collapsedSections.delegations && (
              <Box
                onClick={() => setSelection({ type: "delegations" })}
                sx={{
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border:
                    selection?.type === "delegations"
                      ? "1px solid #93C5FD"
                      : "1px solid #E5E7EB",
                  backgroundColor:
                    selection?.type === "delegations" ? "#EFF6FF" : "#FFFFFF",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor:
                      selection?.type === "delegations" ? "#DBEAFE" : "#F9FAFB",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Handshake size={14} color="#6B7280" />
                  <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                    All delegations ({botDelegations.length})
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {selectedFamily &&
          !detailsLoading &&
          conversations.length === 0 &&
          agentCrons.length === 0 &&
          botDocuments.length === 0 &&
          botTasks.length === 0 &&
          botCalendarEvents.length === 0 &&
          botDelegations.length === 0 && (
            <Typography variant="body" color="text.secondary">
              No bot conversations found.
            </Typography>
          )}
      </Box>
    </Box>
  );

  const cronDetails = selectedAgentCron && (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        flex: 1,
        minWidth: 0,
        padding: "20px",
        border: "1px solid #E5E5E5",
        borderRadius: "8px",
        backgroundColor: "#FAFAFA",
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Typography variant="h3">{selectedAgentCron.title}</Typography>
          {selectedAgentCron.cronType && (
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color:
                  selectedAgentCron.cronType === "system"
                    ? "#7C3AED"
                    : selectedAgentCron.cronType === "admin"
                      ? "#D97706"
                      : "#6B7280",
                backgroundColor:
                  selectedAgentCron.cronType === "system"
                    ? "#F5F3FF"
                    : selectedAgentCron.cronType === "admin"
                      ? "#FFFBEB"
                      : "#F3F4F6",
                borderRadius: "4px",
                padding: "2px 8px",
                flexShrink: 0,
              }}
            >
              {selectedAgentCron.cronType}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => setEditingCron(selectedAgentCron)}
              sx={{ padding: "4px" }}
            >
              <Pencil size={15} color="#6B7280" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Deactivate">
            <IconButton
              size="small"
              onClick={() =>
                confirmState.openDialog(async () => {
                  await adminUpdateAgentCron(
                    new AdminUpdateAgentCronRequest({
                      agentCronRef: selectedAgentCron.agentCronRef,
                      deactivate: true,
                    }),
                  );
                  if (selectedFamily?.ref) loadDetails(selectedFamily.ref);
                })
              }
              sx={{ padding: "4px" }}
            >
              <Trash2 size={15} color="#6B7280" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Typography variant="body" color="text.secondary">
        {selectedAgentCron.scheduleSummary || "No schedule info"}
      </Typography>
      {selectedAgentCron.goal && (
        <Typography variant="body" sx={{ whiteSpace: "pre-wrap" }}>
          {selectedAgentCron.goal}
        </Typography>
      )}
      {selectedAgentCron.agentThreadRef && (
        <Box
          onClick={() => setDebugThreadRef(selectedAgentCron.agentThreadRef)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            cursor: "pointer",
            width: "fit-content",
            "&:hover": { opacity: 0.7 },
          }}
        >
          <Bug size={14} color="#9CA3AF" />
          <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
            Inspect latest execution
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          mt: "4px",
        }}
      >
        <Typography variant="bodySmall" color="text.secondary">
          Recent Executions
        </Typography>
        {selectedAgentCron.executions.length > 0 ? (
          selectedAgentCron.executions.map((execution) => {
            const statusLabel =
              EXECUTION_STATUS_COPY[execution.status] || execution.status;
            const statusColor =
              EXECUTION_STATUS_COLOR[execution.status] || "#6B7280";
            return (
              <Box
                key={execution.agentThreadRef}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  padding: "10px 12px",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  backgroundColor: "#FFFFFF",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    <CircleCheckBig size={14} color={statusColor} />
                    <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                      {statusLabel}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                    <DateDisplay
                      date={new Date(Number(execution.createdSec) * 1000)}
                    />
                  </Typography>
                </Box>
                <Box
                  onClick={() => setDebugThreadRef(execution.agentThreadRef)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    cursor: "pointer",
                    flexShrink: 0,
                    "&:hover": { opacity: 0.7 },
                  }}
                >
                  <Bug size={14} color="#9CA3AF" />
                  <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                    Inspect
                  </Typography>
                </Box>
              </Box>
            );
          })
        ) : (
          <Typography variant="body" color="text.secondary">
            This scheduled action has not executed yet.
          </Typography>
        )}
      </Box>
    </Box>
  );

  const documentView = selectedDocumentRef && (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        flex: 1,
        minWidth: 0,
        padding: "20px",
        border: "1px solid #E5E5E5",
        borderRadius: "8px",
        // if document mode, make a shade of white
        backgroundColor: selectedDocumentRef ? "#FFFFFF" : "#FAFAFA",
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h3">{documentTitle}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box
            onClick={() => navigate(`/documents/${selectedDocumentRef}`)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              cursor: "pointer",
              "&:hover": { opacity: 0.7 },
            }}
          >
            <ExternalLink size={14} color="#6B7280" />
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
              Open document
            </Typography>
          </Box>
          <Box
            onClick={handleDeleteDocument}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              "&:hover": { opacity: 0.7 },
            }}
          >
            <Trash2 size={14} color="#6B7280" />
          </Box>
        </Box>
      </Box>
      {documentLoading && (
        <Typography variant="body" color="text.secondary">
          Loading document...
        </Typography>
      )}
      {documentContent !== null &&
        selectedDocument &&
        (selectedDocument.documentType === DocumentType.LIST ? (
          <ListDocumentView
            key={selectedDocumentRef}
            document={selectedDocument}
            title={documentTitle}
            content={documentContent}
            initialAttachments={documentAttachments}
            embedded={true}
          />
        ) : (
          <DocumentViewer
            key={selectedDocumentRef}
            content={documentContent}
            attachments={documentAttachments}
          />
        ))}
    </Box>
  );

  const feed = (
    <Box
      ref={feedContainerRef}
      sx={{
        display: "flex",
        flexDirection: "column-reverse",
        flex: 1,
        minWidth: 0,
        overflowY: "auto",
        padding: "12px 16px",
        border: "1px solid #E5E5E5",
        borderRadius: "8px",
        backgroundColor: "#FAFAFA",
      }}
    >
      {feedLoading && entries.length === 0 && (
        <Typography variant="body" color="text.secondary" sx={{ py: "16px" }}>
          Loading messages...
        </Typography>
      )}
      {sortedEntries.map((entry, i) => {
        const threadRef = agentThreadRefs[entry.ref];
        const entryTasks = tasksByMessage[entry.ref];
        const entryEvents = eventsByMessage[entry.ref];
        return (
          <React.Fragment key={`${entry.medium}-${entry.ref}`}>
            {threadRef && (
              <Box
                onClick={() => setDebugThreadRef(threadRef)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "3px",
                  justifyContent: "flex-start",
                  mr: "4px",
                  mb: "4px",
                  cursor: "pointer",
                  "&:hover": { opacity: 0.7 },
                }}
              >
                <Bug size={11} color="#9CA3AF" />
                <Typography
                  sx={{ fontSize: "11px", color: "#9CA3AF", lineHeight: 1 }}
                >
                  inspect
                </Typography>
              </Box>
            )}
            {entryTasks?.map((t) => (
              <Box
                key={`task-${t.taskRef}`}
                onClick={() => navigate(`/tasks/${t.taskRef}`)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  ml: "12px",
                  mb: "4px",
                  py: "4px",
                  px: "8px",
                  borderRadius: "6px",
                  backgroundColor: "#F0FDF4",
                  border: "1px solid #BBF7D0",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#DCFCE7" },
                }}
              >
                <CircleCheckBig size={13} color="#16A34A" />
                <Typography sx={{ fontSize: "12px", color: "#15803D" }}>
                  Created task: {t.title}
                </Typography>
              </Box>
            ))}
            {entryEvents?.map((e) => (
              <Box
                key={`event-${e.eventRef}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  ml: "12px",
                  mb: "4px",
                  py: "4px",
                  px: "8px",
                  borderRadius: "6px",
                  backgroundColor: "#EFF6FF",
                  border: "1px solid #BFDBFE",
                }}
              >
                <Calendar size={13} color="#2563EB" />
                <Typography sx={{ fontSize: "12px", color: "#1D4ED8" }}>
                  Created event: {e.title}
                </Typography>
              </Box>
            ))}
            <FeedEntryComponent
              entry={entry}
              feedFocusState={feedFocusState}
              setFeedFocusState={setFeedFocusState}
              accountMap={accountMap}
              openAttachment={(attachment) => setOpenAttachment(attachment)}
              nextEntry={sortedEntries[i - 1]}
              prevEntry={sortedEntries[i + 1]}
              noAvatars={true}
              botExpanded={true}
              botColorScheme={BOT_COLORS}
              formatMarkdown={true}
            />
          </React.Fragment>
        );
      })}
      {hasMore && (
        <Box sx={{ textAlign: "center", py: "8px" }}>
          <Typography
            variant="bodySmall"
            sx={{
              color: "#198282",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={handleLoadMore}
          >
            Load older messages
          </Typography>
        </Box>
      )}
    </Box>
  );

  const emptyState = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        minWidth: 0,
        padding: "40px 20px",
        gap: "12px",
      }}
    >
      <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#6B7280" }}>
        Select a family to view bot activity
      </Typography>
      <Typography
        sx={{ fontSize: "14px", color: "#9CA3AF", textAlign: "center" }}
      >
        Choose a family from the dropdown to see their bot conversations,
        documents, and scheduled actions.
      </Typography>
    </Box>
  );

  const TASK_STATE_COLOR: Record<string, string> = {
    open: "#2563EB",
    inprogress: "#D97706",
    complete: "#15803D",
    deleted: "#6B7280",
  };

  const DELEGATION_STATUS_COLOR: Record<string, string> = {
    queued: "#6B7280",
    in_progress: "#1D4ED8",
    delegated: "#7C3AED",
    completed: "#15803D",
    cancelled: "#B91C1C",
  };

  const listPanelSx = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    flex: 1,
    minWidth: 0,
    padding: "20px",
    border: "1px solid #E5E5E5",
    borderRadius: "8px",
    backgroundColor: "#FAFAFA",
    overflowY: "auto",
  };

  const tasksView = selection?.type === "tasks" && (
    <Box sx={listPanelSx}>
      <Typography variant="h3" sx={{ mb: "4px" }}>
        Bot Tasks ({botTasks.length})
      </Typography>
      {botTasks.map((task) => (
        <Box
          key={task.taskRef}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            padding: "10px 12px",
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              minWidth: 0,
            }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: 500 }} noWrap>
              {task.title}
            </Typography>
            <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: TASK_STATE_COLOR[task.state] || "#6B7280",
                }}
              >
                {task.state}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#9CA3AF" }}>
                ·
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                {task.priority}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#9CA3AF" }}>
                ·
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                <DateDisplay date={new Date(Number(task.createdSec) * 1000)} />
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {task.agentThreadRef && (
              <Box
                onClick={() => setDebugThreadRef(task.agentThreadRef)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer",
                  "&:hover": { opacity: 0.7 },
                }}
              >
                <Bug size={13} color="#9CA3AF" />
              </Box>
            )}
            <Box
              onClick={() => navigate(`/tasks/${task.taskRef}`)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                "&:hover": { opacity: 0.7 },
              }}
            >
              <ExternalLink size={13} color="#6B7280" />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );

  const calendarEventsView = selection?.type === "calendarEvents" && (
    <Box sx={listPanelSx}>
      <Typography variant="h3" sx={{ mb: "4px" }}>
        Bot Calendar Events ({botCalendarEvents.length})
      </Typography>
      {botCalendarEvents.map((event) => (
        <Box
          key={event.eventRef}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            padding: "10px 12px",
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              minWidth: 0,
            }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: 500 }} noWrap>
              {event.title}
            </Typography>
            <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {event.startSec > 0 && (
                <>
                  <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                    <DateDisplay
                      date={new Date(Number(event.startSec) * 1000)}
                    />
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#9CA3AF" }}>
                    ·
                  </Typography>
                </>
              )}
              <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                Created{" "}
                <DateDisplay date={new Date(Number(event.createdSec) * 1000)} />
              </Typography>
            </Box>
          </Box>
          {event.agentThreadRef && (
            <Box
              onClick={() => setDebugThreadRef(event.agentThreadRef)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                flexShrink: 0,
                "&:hover": { opacity: 0.7 },
              }}
            >
              <Bug size={13} color="#9CA3AF" />
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );

  const delegationsView = selection?.type === "delegations" && (
    <Box sx={listPanelSx}>
      <Typography variant="h3" sx={{ mb: "4px" }}>
        Bot Delegations ({botDelegations.length})
      </Typography>
      {botDelegations.map((d) => (
        <Box
          key={d.delegationRef}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            padding: "10px 12px",
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              minWidth: 0,
            }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: 500 }} noWrap>
              {d.title}
            </Typography>
            <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: DELEGATION_STATUS_COLOR[d.status] || "#6B7280",
                }}
              >
                {d.status.replace("_", " ")}
              </Typography>
              {d.serviceType && (
                <>
                  <Typography sx={{ fontSize: "12px", color: "#9CA3AF" }}>
                    ·
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                    {d.serviceType}
                  </Typography>
                </>
              )}
              <Typography sx={{ fontSize: "12px", color: "#9CA3AF" }}>
                ·
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                <DateDisplay date={new Date(Number(d.createdSec) * 1000)} />
              </Typography>
            </Box>
          </Box>
          {d.taskRef && (
            <Box
              onClick={() => navigate(`/tasks/${d.taskRef}`)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                flexShrink: 0,
                "&:hover": { opacity: 0.7 },
              }}
            >
              <ExternalLink size={13} color="#6B7280" />
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );

  const mainContent = () => {
    if (!selectedFamily) return emptyState;
    if (selection?.type === "agentCron") return cronDetails;
    if (selection?.type === "document") return documentView;
    if (selection?.type === "tasks") return tasksView;
    if (selection?.type === "calendarEvents") return calendarEventsView;
    if (selection?.type === "delegations") return delegationsView;
    return feed;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 50px)",
        overflow: "auto",
        padding: isMobile ? "12px" : "24px 32px",
        maxWidth: "1000px",
        margin: "0 auto",
        width: "100%",
      }}
    >
      <Typography variant="display" sx={{ mb: "16px", flexShrink: 0 }}>
        Bot Conversations
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 0,
          flex: 1,
          minHeight: 0,
          maxHeight: "calc(100vh - 150px)",
        }}
      >
        {sidebar}
        <Box
          sx={{
            width: "1px",
            backgroundColor: "#E5E5E5",
            flexShrink: 0,
            mx: "20px",
            display: isMobile ? "none" : "block",
          }}
        />
        {mainContent()}
      </Box>
      <AttachmentDialog
        attachment={openAttachment}
        onClose={() => setOpenAttachment(null)}
      />
      <ConfirmationDialog
        content={"Are you sure? This action cannot be undone."}
        {...confirmState.dialogProps}
      />
      {debugThreadRef && (
        <AgentDebugTool
          threadRef={debugThreadRef}
          open={true}
          onClose={() => setDebugThreadRef(null)}
        />
      )}
      {showCreateCron && selectedFamily && (
        <CreateAgentCronDialog
          familyRef={selectedFamily.ref}
          loading={createCronLoading}
          onClose={() => setShowCreateCron(false)}
          onCreate={async (params) => {
            const resp = await adminCreateAgentCron(params);
            if (resp?.agentCron) {
              setShowCreateCron(false);
              loadDetails(selectedFamily.ref, false);
            }
          }}
        />
      )}
      {editingCron && selectedFamily && (
        <UpdateAgentCronDialog
          agentCron={editingCron}
          loading={updateCronLoading}
          onClose={() => setEditingCron(null)}
          onUpdate={async (params) => {
            const resp = await adminUpdateAgentCron(params);
            if (resp?.agentCron) {
              setEditingCron(null);
              loadDetails(selectedFamily.ref, false);
            }
          }}
        />
      )}
    </Box>
  );
};
